import React, { useCallback, useMemo } from "react"
import { Ship } from "../../App/Ship"
import { GetApp } from "../../App"
import { GetShip, GetTrip } from "../../App/Context"
import { Block, BlockContainer, BlockField, BlockFieldInputsDivider, BlockRow } from "../../Components/Block"
import { Icon, Icons } from "../../Components/Icon"
import { IShipDataSail, IShipStatus, IStatusType } from "../../Types"
import { LogCommentText, LogNMEAContext } from "../Log/nmea"
import { LogNavigationViewData } from "../Log/NavigationView"
import { statusesCoveringLog } from "../Log/status"
import { GetShipUsers } from "../ShipView"
import { computeEditHistory } from "../../App/EditHistory"
import { EditHistory } from "../../Components/EditHistory"
import { currentLogFieldValue, LogViewData } from "../LogView"
import { GetPrintViewContext } from "./Context"
import { PrintLogViewProps } from "./Params"

export function PrintLogView({ status, statuses = [], log, logs, day, auditLogEdits }: PrintLogViewProps) {
  const app = GetApp()!;
  const [ship] = [GetShip()];
  const trip = GetTrip()?.trip;
  const shipUsers = GetShipUsers();
  const printContext = GetPrintViewContext();
  const includeEdits = printContext?.includeEdits ?? false;
  const ft = app.getT("LogNavigationView");
  const fe = app.getT("LogEnvironmentView");
  const fw = app.getT("LogWatchView");

  const statusData = useMemo(() => {
    return LogNavigationViewData.statusData(status);
  }, [status]);

  const engineStatuses = useMemo(() => {
    return LogNavigationViewData.engineStatuses(statuses);
  }, [statuses]);

  const engines = useMemo(() => {
    return LogNavigationViewData.engines(ship, log, engineStatuses);
  }, [engineStatuses, ship, log]);

  const courseToWind = useMemo(() => {
    return LogNavigationViewData.courseToWind(log.heading, log.windDirection);
  }, [log.heading, log.windDirection]);

  const courseToWindText = useMemo(() => {
    if (!courseToWind) {
      return "";
    }
    return [
      app.format("angle", courseToWind.angle),
      ft(`point_of_sail_${courseToWind.pointOfSail}`),
      courseToWind.side ? ft(`wind_side_${courseToWind.side}`) : undefined,
    ].filter(Boolean).join(" | ");
  }, [courseToWind]);

  const comment = useMemo(() => {
    return LogCommentText(log, statuses, { app, ship, trip, shipUsers, logs, day, auditLogEdits } satisfies LogNMEAContext);
  }, [log, statuses, app, ship, trip, shipUsers, logs, day, auditLogEdits]);

  const editHistory = useMemo(() => {
    if (!includeEdits || !auditLogEdits) {
      return [];
    }
    return computeEditHistory(
      auditLogEdits.filter((edit) => edit.logId == log.id),
      (field) => currentLogFieldValue(log, field),
    );
  }, [includeEdits, auditLogEdits, log]);
  const editHistoryFieldFormat = useMemo(() => LogViewData.fieldFormat(app), [app]);
  const editHistoryFieldLabel = useMemo(() => LogViewData.fieldLabel(app), [app]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const _seaState = ((log as any).seaState ?? (log as any).seastate ?? (log as any).sea_state ?? "");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const weather = ((log as any).weather ?? (log as any).weather_state ?? (log as any).weatherState ?? "");

  const sailStatuses = useMemo(() => {
    return statusesCoveringLog((statuses || []).filter((s) => s.statusType === IStatusType.sails), log.id);
  }, [statuses, log.id]);

  const sailName = useCallback((id: string, reef: number, sails: IShipDataSail[]): string => {
    return LogNavigationViewData.sailName(app, id, reef, sails);
  }, [app]);

  // Derive sail status values from statuses
  const sailStatusValues: React.ReactNode = useMemo(() => {
    if (sailStatuses.length === 0) return "";
    const sails = Ship.sails(ship);
    return sailStatuses.map((status) => {
      if (!status.name) return "";
      return sailName(status.name, status.value, sails);
    }).filter(Boolean).join(", ");
  }, [sailStatuses, ship, sailName, app]);

  function rpmValue(n: number): string {
    if (!log.engineRpms || log.engineRpms[n] == undefined || log.engineRpms[n] <= 1) {
      return "";
    }
    return log.engineRpms[n].toFixed(0);
  }

  return (
    <BlockContainer className="PrintLogView">

      <Block className="LogView-navigation">
        <BlockRow className="">
          <BlockField label={ft("status")} className="block-field-120 print-text-bold">
            {status && (
              <>
                <Icon icon={`status-${IShipStatus[status.value]}`} /> {ft("status_" + IShipStatus[status.value])}
              </>
            )}
          </BlockField>
          <BlockField label={ft("time")} className="block-field-45 print-text-bold">{app.format("time", log.time)}</BlockField>
          <BlockField label={ft("time_utc")} className="block-field-45">{app.format("timeUtc", log.timeUtc)}</BlockField>
          <BlockField label={ft("position")} className="block-field-180">
            <span className="print-multi-fields">{app.format("gps-lat", log.lat)}</span> <BlockFieldInputsDivider /> <span className="print-multi-fields">{app.format("gps-lng", log.lng)}</span>
          </BlockField>
        </BlockRow>

        <BlockRow>
          <BlockField label={ft("course")} className="block-field-75">{app.format("course", log.course)}</BlockField>
          <BlockField label={ft("heading")} className="block-field-90">{app.format("course", log.heading)}</BlockField>
          <BlockField label={ft("sog")} className="block-field-75">{app.format("speed", log.sog)}</BlockField>
          <BlockField label={ft("stw")} className="block-field-75">{app.format("speed", log.stw)}</BlockField>
        </BlockRow>

        <BlockRow>
          <BlockField label={ft("distance")} className="block-field-75">{app.format("distance", log.distance)}</BlockField>
          <BlockField label={ft("log")} className="block-field-90">{app.format("distance", log.log)}</BlockField>
          <BlockField label={ft("depth")} className="block-field-75">{app.format("meters", log.depth)}</BlockField>
        </BlockRow>

        <BlockRow>
          {Ship.isSailingVessel(ship) && (
            <BlockField label={ft("sails")} className="block-field-240">
              {sailStatusValues }
            </BlockField>
          )}
          {Ship.isSailingVessel(ship) && courseToWind && (
            <BlockField label={ft("course_to_wind")} className="course-to-wind block-field-165">
              {courseToWindText}
            </BlockField>
          )}
          <BlockField label={[...engines.map((engine) => ft("engine_name", { name: engine.name || `#${engine.number || engine.nmeaNumber}` }))].join(" | ")}>
            {engines.map((engine, i) => {
              const n = engine.nmeaNumber || -1;
              const rpm = rpmValue(n);
              return (
                <span key={i}>
                  <Icon icon={!engine.on ? Icons.CircleLarge : (engine.idle ? Icons.CircleHalfLarge : Icons.CircleFilledLarge)} /> <span className="print-multi-fields">{rpm}</span> {i < ship.engineCount - 1 && <BlockFieldInputsDivider className="print-divider-distance" /> }
                </span>
              );
            })}
          </BlockField>
        </BlockRow>

        <BlockRow>
          <BlockField label={ft("comment")} className="">{comment}</BlockField>
        </BlockRow>
        {status && status.value != IShipStatus.inmotion && status.value != IShipStatus.drifting && (status.startLogId == log.id) && statusData?.location && (
          <BlockRow>
            <BlockField label={ft("location")}>
              {statusData.location}
            </BlockField>
          </BlockRow>
        )}
      </Block>

      <Block className="LogView-environment">
        <BlockRow>
          <BlockField label={fe("wind_direction_speed")} className="block-field-150">
            <span className="print-multi-fields">{app.format("angle", log.windDirection)} </span> <BlockFieldInputsDivider /> <span className="print-multi-fields">{app.format("speed", log.windSpeed)}</span>
          </BlockField>
          <BlockField label={fe("pressure")} className="block-field-120">{app.format("pressure", log.pressure)}</BlockField>
        </BlockRow>

        <BlockRow>
          <BlockField label={fe("weather_temp_rel_humidity")} className=""><span className="print-multi-fields">
            <Icon icon={"weather-" + weather} />  {fe("weather_" + weather)}</span> <BlockFieldInputsDivider /> <span className="print-multi-fields"> {app.format("temperature", log.temperature)} </span><BlockFieldInputsDivider /> <span className="print-multi-fields"> {app.format("percentage", log.humidity)}</span>
          </BlockField>
        </BlockRow>

        <BlockRow>
          <BlockField label={fe("current_direction_speed")} className="block-field-150"><span className="print-multi-fields">{log.currentDirection ? fe(`current_direction_${log.currentDirection}`) || log.currentDirection : ""}</span> <BlockFieldInputsDivider /> <span className="print-multi-fields"> {app.format("speed", log.currentSpeed)}</span></BlockField>
          <BlockField label={fe("seastate")} className="">{log.seaState ? fe(`seastate_${log.seaState}`) || log.seaState : ""}</BlockField>
        </BlockRow>

        <BlockRow className="">
          <BlockField label={fw("watch")}>{log.watch}</BlockField>
        </BlockRow>

        <BlockRow>
          <BlockField label={fw("watch_comment")}>{log.watchComment}</BlockField>
        </BlockRow>

        <BlockRow className="log-timestamps">
          <BlockField label={ft("created")}>{log.createdAt}</BlockField>
        </BlockRow>
        <BlockRow className="log-timestamps">
          <BlockField label={ft("updated")}>{log.updatedAt}</BlockField>
        </BlockRow>
      </Block>
      <EditHistory
        entries={editHistory}
        fieldLabel={editHistoryFieldLabel}
        fieldFormat={editHistoryFieldFormat}
        open={includeEdits}
      />
    </BlockContainer>
  );
}
